import { useTranslation } from 'react-i18next';
import { ChessResult, type OutcomeLabels, type OutcomeView, type Player } from '@qlash-it/qchess-board-ui';

import { Button } from '@/components/common/Button';
import { useCountdown } from '@/hooks/useCountdown';
import { PARTITE_DELLA_DECISIVA } from '@/lib/chess/tavolo';

/**
 * La fine di una partita sul tavolo (reference 07/10, punto 9): un velo sulla scacchiera (nero al 50%, sfocato) con una card.
 *  · a FINE INCONTRO: «La partita è finita.», l'esito in Unbounded 24 e il MOTIVO SU UNA RIGA SUA, e l'uscita;
 *  · FRA UNA PARTITA E L'ALTRA della decisiva: «Partita n di 3 · finita», l'esito, il conto alla `nextGameAt`, i colori
 *    invertiti e la frase sull'Armageddon. Verso l'Armageddon (la prossima e' la terza) la card porta il titolo e la riga
 *    dell'Armageddon: e' una PROPOSTA di copy di qchess-orcr (14:15Z), dichiarata nella PR.
 *
 * 🔴 «Hai vintoper» era `ChessResult` senza stile: due `<span data-part>` fratelli (CONTRATTO §3). Qui il titolo e il motivo
 * sono due BLOCCHI (`[&_[data-part=…]]:block`), e il test li misura come due nodi distinti.
 * ⛔ Il motivo non porta mai i NOMI dei giocatori. «Rivedi la partita» e «Analizza su Lichess» non ci sono ancora: sono P6
 * (replay, ADR-0193 §3.7), e un bottone che non porta da nessuna parte non si disegna.
 */
export function EsitoPartita({
  esito,
  etichette,
  fra,
  uscita,
}: {
  esito: OutcomeView;
  etichette: OutcomeLabels;
  /** Se c'e', non e' la fine dell'incontro: la partita `numero` e' finita e la prossima comincia a `nextGameAt`. */
  fra?: {
    numero: number;
    nextGameAt: string | null;
    /** Il MIO colore nella partita che comincia (l'opposto di quello appena giocato). */
    colore: Player | null;
    /** La prossima e' l'Armageddon (R4). */
    armageddon: boolean;
    /** La prossima e' la ripetizione: se e' ancora patta, si gioca l'Armageddon. */
    avvisoArmageddon: boolean;
  };
  uscita: { etichetta: string; onClick: () => void };
}) {
  const { t } = useTranslation('chessMatch');
  const conto = useCountdown(fra?.nextGameAt ?? null);
  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-black/50 p-3 backdrop-blur-sm"
      data-testid="chess-esito"
      data-fase={fra ? 'tra-le-partite' : 'fine'}
    >
      <div className="flex w-full max-w-[330px] flex-col items-center gap-2 rounded-2xl border border-border bg-bg-secondary p-5 text-center shadow-xl">
        <span className="text-xs font-semibold text-fg-muted">
          {fra ? t('between.gameFinished', { n: fra.numero, total: PARTITE_DELLA_DECISIVA }) : t('end.finished')}
        </span>
        <ChessResult
          outcome={esito}
          labels={etichette}
          className="flex flex-col items-center gap-1 [&_[data-part=headline]]:block [&_[data-part=headline]]:font-display [&_[data-part=headline]]:text-2xl [&_[data-part=headline]]:font-bold [&_[data-part=headline]]:leading-tight [&_[data-part=reason]]:block [&_[data-part=reason]]:text-sm [&_[data-part=reason]]:text-fg-muted"
        />
        {fra ? (
          <>
            <div className="mt-1 flex w-full flex-col items-center gap-0.5 rounded-xl bg-bg-tertiary px-3 py-2.5" data-testid="chess-prossima">
              {fra.armageddon && (
                <span className="font-display text-sm font-bold text-warning" data-testid="chess-prossima-armageddon">
                  {t('between.armageddonTitle')}
                </span>
              )}
              <span className="text-[13px] font-semibold text-fg">{t('between.nextGameIn', { n: fra.numero + 1 })}</span>
              <span className="text-[28px] font-extrabold tabular-nums text-brand">{conto ?? '0:00'}</span>
              {fra.armageddon && <span className="text-[13px] text-fg-muted">{t('between.armageddonNext')}</span>}
              {fra.colore !== null && (
                <span className="text-[13px] text-fg-muted">
                  {t(fra.colore === 'W' ? 'between.colorsSwappedWhite' : 'between.colorsSwappedBlack')}
                </span>
              )}
            </div>
            {fra.avvisoArmageddon && (
              <span className="text-xs leading-snug text-fg-muted" data-testid="chess-armageddon">
                {t('between.armageddonRule')}
              </span>
            )}
          </>
        ) : (
          <div className="mt-2 flex w-full flex-col gap-2">
            <Button fullWidth peso="bold" onClick={uscita.onClick} data-action="uscita">
              {uscita.etichetta}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
